import { ItemsEntity, Uploader } from '@citrite/sf-api';
import { ProjectFileDto, ResourceEntityType } from '@sharefiledev/projects-pilet-sdk';
import {
	FilesCapabilityActions,
	UploadOptions,
	UploadSuccessfulItem,
} from '@sharefiledev/sfplugin-capability-contracts';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import { ProjectDataProvider } from '../PluginDataProvider/ProjectDataProvider';
import { ProjectsNetworkError } from '../ProjectsNetworkError';

export async function uploadFiles(
	dataProvider: ProjectDataProvider,
	requestingResourceId: string,
	files: File[],
	options?: UploadOptions
): ReturnType<FilesCapabilityActions['uploadFiles']> {
	const requestingRid = RID.parse(requestingResourceId);
	const projectRid = requestingRid.findEntityType(ResourceEntityType.project);
	if (!projectRid) {
		throw new Error(`No project resource found for: ${requestingResourceId}`);
	}

	const zoneServiceValue = await dataProvider.getProjectZoneServices(projectRid.entityId);

	const itemUploads = files.map(file => ({
		file,
		itemUpload: Uploader.createItemUpload(
			file,
			item =>
				({
					execute: () =>
						dataProvider
							.uploadFile(projectRid.entityId, requestingResourceId, {
								...item.uploadRequestParams,
								Overwrite: options?.overwrite,
							})
							.catch(error => {
								if (error instanceof ProjectsNetworkError) {
									if (error.status === 409) {
										options?.onConflict?.(item, file);
									}
									throw { status: error.status }; // prevents sf-api from retrying the upload for certain status codes
								}
								throw error;
							}),
				} as any),
			undefined,
			zoneServiceValue
		) as Uploader.IItemUpload,
	}));
	itemUploads.forEach(u => {
		const emitter = Uploader.addItemsToQueue(u.itemUpload);
		listenToUploaderEvents(dataProvider, requestingRid, emitter, options);
	});
	return itemUploads;
}

function listenToUploaderEvents(
	dataProvider: ProjectDataProvider,
	requestingRid: RID,
	emitter: Uploader.UploadStatusEmitter,
	options?: UploadOptions
) {
	if (options?.onProgress) {
		emitter.on('progress', e => {
			let percent = 0;
			try {
				percent = parseInt(e.percent.replace('%', ''), 10);
			} catch {}
			options.onProgress?.({
				currentUploadId: e.currentFile.uploadId,
				uploadBatchId: '',
				percent,
			});
		});
	}
	if (options?.onSuccess) {
		emitter.on('success', async uploads => {
			const projectRid = requestingRid.findEntityType(ResourceEntityType.project)!;

			const successfulFiles: UploadSuccessfulItem<ProjectFileDto>[] = [];
			const erroredFiles: Uploader.IItemUpload[] = [];

			for (const itemUpload of uploads) {
				const itemUrl = ItemsEntity.get(itemUpload.shareFileId).getUrl();
				const item = await ItemsEntity.get(itemUrl).execute();

				const projectFile = await dataProvider
					.uploadFileComplete(
						projectRid.entityId,
						options.resourceId ?? requestingRid.toString(),
						{
							UploadedFileItems: [{ Filename: item.Name, StreamId: item.StreamID }],
						}
					)
					.catch(() => undefined);

				if (projectFile) {
					const resourceId = requestingRid
						.createChild(ResourceEntityType.file, projectFile.fileId)
						.createChild(ResourceEntityType.fileStream, projectFile.shareFileStreamId)
						.toString();
					successfulFiles.push({
						resourceId,
						itemUpload,
						metadata: projectFile,
					});
				} else {
					erroredFiles.push(itemUpload);
				}
			}

			if (erroredFiles.length) {
				options.onError?.(erroredFiles);
			}
			options.onSuccess?.(successfulFiles);
		});
	}
	if (options?.onError) {
		emitter.on('error', items => {
			options.onError?.(items);
		});
	}
}
